import React, { useEffect, useState, useMemo } from "react";
import "../Common/Style/RequestLog.css";

interface Log {
  id: number;
  schoolcode: string;
  familycode: string;
  login: string;
  path: string;
  method: string;
  headers?: string;
  body?: string;
  ip?: string;
  useragent?: string;
  referer?: string;
  host?: string;
  errormessage?: string;
  traceback?: string;
  logatusertime: string;
  logatactualtime: string;
}

const RequestLog: React.FC = () => {
  const [logs, setLogs] = useState<Log[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState("");
  const [methodFilter, setMethodFilter] = useState("all");
  const [errorFilter, setErrorFilter] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [itemsPerPage, setItemsPerPage] = useState(10);
  const [selectedLog, setSelectedLog] = useState<Log | null>(null);
  const [showDetails, setShowDetails] = useState(false);
  const [sortField, setSortField] = useState<keyof Log>("logatactualtime");
  const [sortDirection, setSortDirection] = useState<"asc" | "desc">("desc");
  const apiUrl =
    process.env.REACT_APP_DOCUMENT_LINK ;
  useEffect(() => {
    fetchLogs();
  }, []);

  const fetchLogs = () => {
    setLoading(true);
    setError(null);
    
    fetch(`${apiUrl}get_request_logs/`)
      .then((res) => {
        if (!res.ok) {
          throw new Error(`HTTP error! status: ${res.status}`);
        }
        return res.json();
      })
      .then((data) => {
        setLogs(data.data || []);
        setLoading(false);
      })
      .catch((err) => {
        setError(err.message);
        setLoading(false);
      });
  };

const filteredAndSortedLogs = useMemo(() => {
  const search = searchTerm.toLowerCase();

  let filtered = logs.filter((log) => {
    const matchesSearch =
      (log.login ?? "").toLowerCase().includes(search) ||
      (log.path ?? "").toLowerCase().includes(search) ||
      (log.schoolcode ?? "").toLowerCase().includes(search) ||
      (log.ip ?? "").toLowerCase().includes(search) ||
      (log.errormessage ?? "").toLowerCase().includes(search);

    const matchesMethod =
      methodFilter === "all" || log.method === methodFilter;

    const matchesError =
      errorFilter === "all" ||
      (errorFilter === "errors" && !!log.errormessage) ||
      (errorFilter === "success" && !log.errormessage);

    return matchesSearch && matchesMethod && matchesError;
  });

  return filtered;
}, [logs, searchTerm, methodFilter, errorFilter]);


  const paginatedLogs = useMemo(() => {
    const startIndex = (currentPage - 1) * itemsPerPage;
    return filteredAndSortedLogs.slice(startIndex, startIndex + itemsPerPage);
  }, [filteredAndSortedLogs, currentPage, itemsPerPage]);

  const totalPages = Math.ceil(filteredAndSortedLogs.length / itemsPerPage);

  const handleSort = (field: keyof Log) => {
    if (sortField === field) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortField(field);
      setSortDirection('desc');
    }
  };

  if (loading) {
    return (
      <div className="loading-container">
        <div className="loading-content">
          <div className="spinner"></div>
          <p>Loading request logs...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="error-container">
        <div className="error-content">
          <div className="error-header">
            <span className="error-icon">⚠️</span>
            <div>
              <h3>Error Loading Logs</h3>
              <p>{error}</p>
            </div>
          </div>
          <button onClick={fetchLogs} className="retry-btn">
            Try Again
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="logs-container">
      <div className="logs-wrapper">
        {/* Filters */}
        <div className="filters-card">
          <div className="filters-grid">
            <div className="search-container">
              <input
                type="text"
                placeholder="Search logs..."
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="search-input"
              />
            </div>
            {/* <select
              value={methodFilter}
              onChange={(e) => setMethodFilter(e.target.value)}
              className="filter-select"
            >
              <option value="all">All Methods</option>
              <option value="GET">GET</option>
              <option value="POST">POST</option>
              <option value="PUT">PUT</option>
              <option value="DELETE">DELETE</option>
              <option value="PATCH">PATCH</option>
            </select> */}
            <div>
            {/* <button onClick={fetchLogs} className="refresh-btn">
              <span className="refresh-icon">↻</span>
              Refresh
            </button> */}
            </div>
            {/* <div className="stat-card">
            <div className="stat-content">
              <div className="stat-icon total">⏰</div>
              <div className="stat-info">
                <p className="stat-label">Total Requests</p>
                <p className="stat-value">{logs.length}</p>
              </div>
            </div>
          </div> */}
          </div>
        </div>

        {/* Table */}
        <div className="table-card">
          <div className="table-container">
            <table className="logs-table">
              <thead>
                <tr>
                  <th
                    onClick={() => handleSort('id')}
                    className={`sortable ${sortField === 'id' ? 'sorted' : ''}`}
                  >
                    ID {sortField === 'id' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('method')}
                    className={`sortable ${sortField === 'method' ? 'sorted' : ''}`}
                  >
                    Method {sortField === 'method' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('path')}
                    className={`sortable ${sortField === 'path' ? 'sorted' : ''}`}
                  >
                    Path {sortField === 'path' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th
                    onClick={() => handleSort('login')}
                    className={`sortable ${sortField === 'login' ? 'sorted' : ''}`}
                  >
                    User {sortField === 'login' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>School</th>
                  <th>IP</th>
                  <th>Status</th>
                  <th
                    onClick={() => handleSort('logatactualtime')}
                    className={`sortable ${sortField === 'logatactualtime' ? 'sorted' : ''}`}
                  >
                    Time {sortField === 'logatactualtime' && (sortDirection === 'asc' ? '↑' : '↓')}
                  </th>
                  <th>Headers</th>
                  <th>Body</th>
                  <th>Referer</th>
                  <th>Host</th>
                  <th>Traceback</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {paginatedLogs.map((log) => (
                  <tr key={log.id} className="table-row">
                    <td className="font-medium">{log.id}</td>
                    <td>
                      <span className={`method-badge method-${log.method.toLowerCase()}`}>
                        {log.method}
                      </span>
                    </td>
                    <td className="truncate" title={log.path}>{log.path}</td>
                    <td className="truncate" title={log.login}>{log.login}</td>
                    <td>{log.schoolcode}</td>
                    <td>{log.ip}</td>
                    <td>
                      {log.errormessage ? (
                        <span className="status-badge status-error">Error</span>
                      ) : (
                        <span className="status-badge status-success">Success</span>
                      )}
                    </td>
                    <td>
                      {new Date(log.logatactualtime).toLocaleDateString()}{' '}
                      {new Date(log.logatactualtime).toLocaleTimeString()}
                    </td>
                    <td className="truncate" title={log.headers}>
                      {log.headers ? (
                        <span className="data-indicator">{log.headers}</span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                    <td className="truncate" title={log.body}>
                      {log.body ? (
                        <span className="data-indicator">{log.body}</span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                    <td className="truncate" title={log.referer}>
                      {log.referer || <span className="no-data">-</span>}
                    </td>
                    <td className="truncate" title={log.host}>
                      {log.host || <span className="no-data">-</span>}
                    </td>
                    <td className="truncate" title={log.traceback}>
                      {log.traceback ? (
                        <span className="data-indicator error-indicator">{log.traceback}</span>
                      ) : (
                        <span className="no-data">-</span>
                      )}
                    </td>
                    <td>
                      <button
                        onClick={() => {
                          setSelectedLog(log);
                          setShowDetails(true);
                        }}
                        className="view-btn"
                      >
                        👁️ View
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>

          {/* Pagination */}
          <div className="pagination-container">
            <div className="pagination-info">
              <p>
                Showing {((currentPage - 1) * itemsPerPage) + 1} to{' '}
                {Math.min(currentPage * itemsPerPage, filteredAndSortedLogs.length)}{' '}
                of {filteredAndSortedLogs.length} results
              </p>
            </div>
            <div className="pagination-controls">
              <button
                onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                disabled={currentPage === 1}
                className="pagination-btn"
              >
                Previous
              </button>
              {Array.from({ length: Math.min(totalPages, 5) }, (_, i) => {
                const page = i + 1;
                return (
                  <button
                    key={page}
                    onClick={() => setCurrentPage(page)}
                    className={`pagination-btn ${currentPage === page ? 'active' : ''}`}
                  >
                    {page}
                  </button>
                );
              })}
              <button
                onClick={() => setCurrentPage(Math.min(totalPages, currentPage + 1))}
                disabled={currentPage === totalPages}
                className="pagination-btn"
              >
                Next
              </button>
            </div>
          </div>
        </div>

        {/* Detail Modal */}
        {showDetails && selectedLog && (
          <div className="modal-overlay">
            <div className="modal-content">
              <div className="modal-header">
                <h3>Request Details</h3>
                <button
                  onClick={() => setShowDetails(false)}
                  className="close-btn"
                >
                  ✕
                </button>
              </div>
              <div className="modal-body">
                <div className="details-grid">
                  <div className="detail-item">
                    <label>ID</label>
                    <p>{selectedLog.id}</p>
                  </div>
                  <div className="detail-item">
                    <label>Method</label>
                    <p>
                      <span className={`method-badge method-${selectedLog.method.toLowerCase()}`}>
                        {selectedLog.method}
                      </span>
                    </p>
                  </div>
                  <div className="detail-item">
                    <label>Path</label>
                    <p className="break-all">{selectedLog.path}</p>
                  </div>
                  <div className="detail-item">
                    <label>User</label>
                    <p>{selectedLog.login}</p>
                  </div>
                  <div className="detail-item">
                    <label>School Code</label>
                    <p>{selectedLog.schoolcode}</p>
                  </div>
                  <div className="detail-item">
                    <label>Family Code</label>
                    <p>{selectedLog.familycode}</p>
                  </div>
                  <div className="detail-item">
                    <label>IP Address</label>
                    <p>{selectedLog.ip}</p>
                  </div>
                  <div className="detail-item">
                    <label>Actual Time</label>
                    <p>{selectedLog.logatactualtime}</p>
                  </div>
                  <div className="detail-item">
                    <label>User Time</label>
                    <p>{selectedLog.logatusertime}</p>
                  </div>
                  <div className="detail-item">
                    <label>Referer</label>
                    <p>{selectedLog.referer || 'N/A'}</p>
                  </div>
                  <div className="detail-item">
                    <label>Host</label>
                    <p>{selectedLog.host || 'N/A'}</p>
                  </div>
                </div>
                {selectedLog.headers && (
                  <div className="detail-item full-width">
                    <label>Headers</label>
                    <div className="code-block">
                      <p>{selectedLog.headers}</p>
                    </div>
                  </div>
                )}
                {selectedLog.body && (
                  <div className="detail-item full-width">
                    <label>Request Body</label>
                    <div className="code-block">
                      <p>{selectedLog.body}</p>
                    </div>
                  </div>
                )}
                {selectedLog.useragent && (
                  <div className="detail-item full-width">
                    <label>User Agent</label>
                    <p className="break-all">{selectedLog.useragent}</p>
                  </div>
                )}
                {selectedLog.errormessage && (
                  <div className="detail-item full-width">
                    <label>Error Message</label>
                    <p className="error-message">{selectedLog.errormessage}</p>
                  </div>
                )}
                {selectedLog.traceback && (
                  <div className="detail-item full-width">
                    <label>Traceback</label>
                    <div className="code-block error-code">
                      <p>{selectedLog.traceback}</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default RequestLog;